import { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { API_DELETE, API_GET, API_PATCH, API_POST, DBRequest, useConfig } from '../../api/api';
import { hasPermissions } from '../../auth/hasPermissions';
import Cookies from 'universal-cookie';
import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import { playNotificationSound, playInChatSound, playUrgentSound } from './useChatAudio';

// Make Pusher available globally for Laravel Echo
window.Pusher = Pusher;

const cookies = new Cookies();

// Debounce utility
const debounce = (fn, delay) => {
  let timeoutId;
  const debounced = (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
  debounced.cancel = () => clearTimeout(timeoutId);
  return debounced;
};

// Connection status constants
const CONNECTION_STATUS = {
  CONNECTED: 'connected',
  CONNECTING: 'connecting',
  DISCONNECTED: 'disconnected',
  FAILED: 'failed',
};

// Reconnection config
const RECONNECT_CONFIG = {
  maxAttempts: 5,
  baseDelay: 1000,
  maxDelay: 30000,
};

const ChatClientContext = createContext({
  conversations: [],
  selectedConversation: null,
  messages: [],
  unreadTotal: 0,
  isLoading: false,
  typingUsers: {},
  notifications: [],
  replyToMessage: null,
  pendingFiles: null,
  connectionStatus: CONNECTION_STATUS.DISCONNECTED,
  setSelectedConversation: () => {},
  sendMessage: () => {},
  loadMoreMessages: () => {},
  createConversation: () => {},
  findOrCreateDirect: () => {},
  markAsRead: () => {},
  refreshConversations: () => {},
  dismissNotification: () => {},
  setReplyToMessage: () => {},
  addReaction: () => {},
  removeReaction: () => {},
  updateConversation: () => {},
  setPendingFiles: () => {},
});

export const ChatClientProvider = ({ children }) => {
  const config = useConfig();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Compute unreadTotal from conversations - always in sync
  const unreadTotal = useMemo(() => {
    return conversations.reduce((sum, conv) => sum + (conv.unreadCount || 0), 0);
  }, [conversations]);
  const [isLoadingConversations, setIsLoadingConversations] = useState(true);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [nextCursor, setNextCursor] = useState(null);
  const [typingUsers, setTypingUsers] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [replyToMessage, setReplyToMessage] = useState(null);
  const [pendingFiles, setPendingFiles] = useState(null);
  const [urgentMessage, setUrgentMessage] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState(CONNECTION_STATUS.DISCONNECTED);
  const [, setReconnectAttempts] = useState(0); // Value read only via functional updates
  const typingTimeoutRef = useRef({});
  const notificationIdRef = useRef(0);
  const notificationTimeoutsRef = useRef(new Map());
  const echoRef = useRef(null);
  const subscribedConversationsRef = useRef(new Set());
  const selectedConversationRef = useRef(null);
  const conversationsRef = useRef([]);
  const reconnectTimeoutRef = useRef(null);
  const messageIdsRef = useRef(new Set());

  // Check if user is authenticated
  const isAuthenticated = Boolean(cookies.get('accessToken'));
  const currentUserId = cookies.get('userId');

  // Keep refs in sync with state for WebSocket handlers (avoid stale closures)
  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);

  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);

  // Memoized conversation IDs string for efficient dependency tracking
  const conversationIdsKey = useMemo(
    () => conversations.map(c => c.id).join(','),
    [conversations]
  );

  // Mark all messages as read (defined early because it's used in the messages useEffect)
  const markAllAsRead = useCallback((conversationId) => {
    if (!config || !conversationId) return;

    const convIdStr = String(conversationId);

    // Store previous unread count for potential rollback
    const previousUnreadCount = conversationsRef.current.find(
      conv => String(conv.id) === convIdStr
    )?.unreadCount || 0;

    // Update local state immediately for responsive UI (optimistic update)
    // unreadTotal is computed automatically via useMemo from conversations
    setConversations(prev => {
      return prev.map(conv => {
        // Use string comparison to avoid type mismatch issues
        if (String(conv.id) === convIdStr) {
          return { ...conv, unreadCount: 0 };
        }
        return conv;
      });
    });

    // Then make the API call
    DBRequest({
      config,
      path: `chat/conversations/${conversationId}/read-all`,
      method: API_POST({}),
      onError: (err) => {
        console.error('[Chat] Failed to mark messages as read:', err);
        // Rollback on failure
        setConversations(prev => {
          return prev.map(conv => {
            if (String(conv.id) === convIdStr) {
              return { ...conv, unreadCount: previousUnreadCount };
            }
            return conv;
          });
        });
      },
    });
  }, [config]);

  // Fetch conversations
  const hasChatPermission = hasPermissions('chat-message');

  const fetchConversations = useCallback(() => {
    if (!config || !isAuthenticated || !hasChatPermission) return;

    DBRequest({
      config,
      path: 'chat/conversations?perPage=50',
      method: API_GET,
      onResponse: (res) => {
        setConversations(res.data || []);
        // unreadTotal is computed automatically via useMemo
        setIsLoadingConversations(false);
      },
      onError: () => {
        setIsLoadingConversations(false);
      },
    });
  }, [config, isAuthenticated, hasChatPermission]);

  // Initial fetch and polling
  useEffect(() => {
    if (!config || !isAuthenticated || !hasChatPermission) return;

    fetchConversations();

    // Poll for new conversations every 30 seconds
    const interval = setInterval(fetchConversations, 30000);
    return () => clearInterval(interval);
  }, [config, fetchConversations, isAuthenticated, hasChatPermission]);

  // Listen for standalone auth changes (re-login)
  useEffect(() => {
    const handleAuthChange = (event) => {
      if (event.detail?.authenticated && config) {
        // Small delay to ensure cookies are set
        setTimeout(() => {
          setIsLoadingConversations(true);
          fetchConversations();
        }, 100);
      }
    };

    window.addEventListener('standaloneAuthChange', handleAuthChange);
    return () => window.removeEventListener('standaloneAuthChange', handleAuthChange);
  }, [config, fetchConversations]);

  // Fetch messages when conversation changes
  useEffect(() => {
    if (!config || !selectedConversation) {
      setMessages([]);
      messageIdsRef.current.clear();
      setHasMoreMessages(true);
      setNextCursor(null);
      return;
    }

    // Clear messages immediately when switching conversation
    setMessages([]);
    messageIdsRef.current.clear();
    setIsLoading(true);
    setHasMoreMessages(true);
    setNextCursor(null);

    DBRequest({
      config,
      path: `chat/conversations/${selectedConversation.id}/messages?perPage=50`,
      method: API_GET,
      onResponse: (res) => {
        const messages = res.data || [];
        // Populate messageIdsRef with initial message IDs
        messages.forEach(m => messageIdsRef.current.add(m.id));
        setMessages(messages);
        setHasMoreMessages(res.meta?.hasMore || false);
        setNextCursor(res.meta?.nextCursor || null);
        setIsLoading(false);
      },
      onError: () => {
        setIsLoading(false);
      },
    });

    // Mark all as read when opening conversation
    markAllAsRead(selectedConversation.id);
  }, [config, selectedConversation?.id, markAllAsRead]);

  // Load more messages (pagination)
  const loadMoreMessages = useCallback(() => {
    if (!config || !selectedConversation || !hasMoreMessages || isLoading) return;

    setIsLoading(true);
    DBRequest({
      config,
      path: `chat/conversations/${selectedConversation.id}/messages?perPage=50${nextCursor ? `&cursor=${nextCursor}` : ''}`,
      method: API_GET,
      onResponse: (res) => {
        const newMessages = res.data || [];
        // Add new message IDs to the Set
        newMessages.forEach(m => messageIdsRef.current.add(m.id));
        setMessages(prev => [...prev, ...newMessages]);
        setHasMoreMessages(res.meta?.hasMore || false);
        setNextCursor(res.meta?.nextCursor || null);
        setIsLoading(false);
      },
      onError: (err) => {
        console.error('[Chat] Failed to load more messages:', err);
        setIsLoading(false);
      },
    });
  }, [config, selectedConversation, hasMoreMessages, isLoading, nextCursor]);

  // Send message
  const sendMessage = useCallback((content, options = {}) => {
    if (!config || !selectedConversation) return Promise.reject('No conversation selected');

    const { attachments = [], references = [], replyToId = null, isUrgent = false } = options;

    return new Promise((resolve, reject) => {
      DBRequest({
        config,
        path: `chat/conversations/${selectedConversation.id}/messages`,
        method: API_POST({
          content,
          attachments,
          references,
          reply_to_id: replyToId,
          is_urgent: isUrgent,
        }),
        onResponse: (res) => {
          // Add new message to the beginning of the list (check for duplicates using Set)
          setMessages(prev => {
            if (messageIdsRef.current.has(res.data.id)) return prev;
            messageIdsRef.current.add(res.data.id);
            return [res.data, ...prev];
          });
          // Update conversation's last message
          setConversations(prev => prev.map(conv =>
            conv.id === selectedConversation.id
              ? { ...conv, latestMessage: res.data, lastMessageAt: new Date().toISOString() }
              : conv
          ));
          resolve(res.data);
        },
        onError: reject,
      });
    });
  }, [config, selectedConversation]);

  // Create new conversation
  const createConversation = useCallback((type, participantIds, name = null) => {
    if (!config) return Promise.reject('No config');

    return new Promise((resolve, reject) => {
      DBRequest({
        config,
        path: 'chat/conversations',
        method: API_POST({
          type,
          participant_ids: participantIds,
          name,
        }),
        onResponse: (res) => {
          setConversations(prev => [res.data, ...prev]);
          setSelectedConversation(res.data);
          resolve(res.data);
        },
        onError: reject,
      });
    });
  }, [config]);

  // Find or create direct message
  const findOrCreateDirect = useCallback((userId) => {
    if (!config) return Promise.reject('No config');

    return new Promise((resolve, reject) => {
      DBRequest({
        config,
        path: `chat/conversations/direct/${userId}`,
        method: API_GET,
        onResponse: (res) => {
          const conversation = res.data || res;
          // Check if conversation already exists in list
          setConversations(prev => {
            const exists = prev.find(c => c.id === conversation.id);
            if (exists) return prev;
            return [conversation, ...prev];
          });
          setSelectedConversation(conversation);
          resolve(conversation);
        },
        onError: reject,
      });
    });
  }, [config]);

  // Mark message as read
  const markAsRead = useCallback((messageId) => {
    if (!config) return;

    DBRequest({
      config,
      path: `chat/messages/${messageId}/read`,
      method: API_PATCH({}),
    });
  }, [config]);

  // Send typing indicator (debounced to prevent excessive API calls)
  const typingIndicatorRef = useRef(null);

  const sendTypingIndicator = useCallback(() => {
    if (!config || !selectedConversation) return;

    // Create or reuse debounced function
    if (!typingIndicatorRef.current) {
      typingIndicatorRef.current = debounce((convId) => {
        DBRequest({
          config,
          path: `chat/conversations/${convId}/typing`,
          method: API_POST({}),
          onError: (err) => {
            // Silent fail for typing indicators, just log
            console.warn('[Chat] Failed to send typing indicator:', err);
          },
        });
      }, 500);
    }

    typingIndicatorRef.current(selectedConversation.id);
  }, [config, selectedConversation]);

  // Handle incoming typing indicator
  const handleTypingIndicator = useCallback((userId, userName) => {
    setTypingUsers(prev => ({ ...prev, [userId]: userName }));

    // Clear existing timeout for this user
    if (typingTimeoutRef.current[userId]) {
      clearTimeout(typingTimeoutRef.current[userId]);
    }

    // Remove typing indicator after 3 seconds
    typingTimeoutRef.current[userId] = setTimeout(() => {
      setTypingUsers(prev => {
        const next = { ...prev };
        delete next[userId];
        return next;
      });
    }, 3000);
  }, []);

  // Show notification popup
  const showNotification = useCallback((message, conversation) => {
    const notificationId = ++notificationIdRef.current;
    // Get sender name
    const senderName = message.sender?.name ||
      `${message.sender?.firstName || ''} ${message.sender?.lastName || ''}`.trim() ||
      message.senderName ||
      'Unknown';
    // Get sender avatar (try temporaryUrl first, then url, then avatar)
    const senderAvatar = message.sender?.picture?.temporaryUrl ||
      message.sender?.picture?.url ||
      message.sender?.avatar ||
      message.senderAvatar;

    const notification = {
      id: notificationId,
      conversationId: conversation?.id || message.conversationId,
      conversationName: conversation?.type !== 'direct' ? conversation?.name : null,
      senderName,
      senderAvatar,
      content: message.content,
    };

    setNotifications(prev => [notification, ...prev]);

    // Auto-dismiss after 5 seconds (store timeout for cleanup)
    const timeoutId = setTimeout(() => {
      notificationTimeoutsRef.current.delete(notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    }, 5000);
    notificationTimeoutsRef.current.set(notificationId, timeoutId);
  }, []);

  // Dismiss notification
  const dismissNotification = useCallback((notificationId) => {
    // Clear the auto-dismiss timeout if it exists
    const timeoutId = notificationTimeoutsRef.current.get(notificationId);
    if (timeoutId) {
      clearTimeout(timeoutId);
      notificationTimeoutsRef.current.delete(notificationId);
    }
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  }, []);

  // Add reaction to message
  const addReaction = useCallback((messageId, emoji) => {
    if (!config) return Promise.reject('No config');

    return new Promise((resolve, reject) => {
      DBRequest({
        config,
        path: `chat/messages/${messageId}/react`,
        method: API_POST({ emoji }),
        onResponse: (res) => {
          // Update with server response
          const reactions = res.data?.reactions || res.reactions || [];
          setMessages(prev => prev.map(m => {
            if (m.id === messageId) {
              return { ...m, reactions };
            }
            return m;
          }));
          resolve(res);
        },
        onError: reject,
      });
    });
  }, [config]);

  // Remove reaction from message
  const removeReaction = useCallback((messageId, emoji) => {
    if (!config) return Promise.reject('No config');

    return new Promise((resolve, reject) => {
      DBRequest({
        config,
        path: `chat/messages/${messageId}/react/${encodeURIComponent(emoji)}`,
        method: API_DELETE,
        onResponse: (res) => {
          // Update with server response
          const reactions = res.data?.reactions || res.reactions || [];
          setMessages(prev => prev.map(m => {
            if (m.id === messageId) {
              return { ...m, reactions };
            }
            return m;
          }));
          resolve(res);
        },
        onError: reject,
      });
    });
  }, [config]);

  // Add message from external source (WebSocket)
  const addMessage = useCallback((message) => {
    if (message.conversationId === selectedConversation?.id) {
      setMessages(prev => {
        // Check if message already exists (using Set for O(1) lookup)
        if (messageIdsRef.current.has(message.id)) return prev;
        messageIdsRef.current.add(message.id);
        return [message, ...prev];
      });
    }

    // Update conversation list
    setConversations(prev => prev.map(conv => {
      if (conv.id === message.conversationId) {
        const isSelected = selectedConversation?.id === conv.id;
        return {
          ...conv,
          latestMessage: message,
          lastMessageAt: message.createdAt,
          unreadCount: isSelected ? conv.unreadCount : (conv.unreadCount || 0) + 1,
        };
      }
      return conv;
    }));

    if (selectedConversation?.id !== message.conversationId) {
      // unreadTotal is computed automatically via useMemo from conversations
      // Show notification popup for messages not in current conversation
      const conversation = conversations.find(c => c.id === message.conversationId);
      showNotification(message, conversation);
    }
  }, [selectedConversation, conversations, showNotification]);

  // Update message from external source (WebSocket)
  const updateMessage = useCallback((message) => {
    setMessages(prev => prev.map(m => m.id === message.id ? message : m));
  }, []);

  // Delete message from external source (WebSocket)
  const deleteMessage = useCallback((messageId) => {
    messageIdsRef.current.delete(messageId);
    setMessages(prev => prev.filter(m => m.id !== messageId));
  }, []);

  // Update a conversation locally
  const updateConversation = useCallback((conversationId, updates) => {
    setConversations(prev => prev.map(conv => {
      if (conv.id === conversationId) {
        return { ...conv, ...updates };
      }
      return conv;
    }));

    // Also update selectedConversation if it's the current one
    if (selectedConversation?.id === conversationId) {
      setSelectedConversation(prev => prev ? { ...prev, ...updates } : prev);
    }
  }, [selectedConversation]);

  // Dismiss urgent message fullscreen display
  const dismissUrgentMessage = useCallback(() => {
    setUrgentMessage(null);
  }, []);

  // Refresh messages for the current conversation (used after reconnect to fill gaps)
  const refreshCurrentMessages = useCallback(() => {
    if (!config || !selectedConversationRef.current) return;
    console.log('[Chat] Refreshing messages after reconnect for conversation:', selectedConversationRef.current.id);
    DBRequest({
      config,
      path: `chat/conversations/${selectedConversationRef.current.id}/messages?perPage=50`,
      method: API_GET,
      onResponse: (res) => {
        const freshMessages = res.data || [];
        setMessages(prev => {
          const newMessages = freshMessages.filter(m => !messageIdsRef.current.has(m.id));
          newMessages.forEach(m => messageIdsRef.current.add(m.id));
          if (newMessages.length === 0) return prev;
          // Merge: prepend new messages, keep existing older ones
          return [...newMessages, ...prev];
        });
      },
    });
  }, [config]);

  // Schedule reconnection with exponential backoff
  const scheduleReconnect = useCallback((attempts) => {
    if (attempts >= RECONNECT_CONFIG.maxAttempts) {
      console.log('[Chat] Max reconnect attempts reached, giving up');
      setConnectionStatus(CONNECTION_STATUS.FAILED);
      return;
    }

    const delay = Math.min(
      RECONNECT_CONFIG.baseDelay * Math.pow(2, attempts),
      RECONNECT_CONFIG.maxDelay
    );
    console.log(`[Chat] Scheduling reconnect attempt ${attempts + 1} in ${delay}ms`);

    reconnectTimeoutRef.current = setTimeout(() => {
      if (echoRef.current?.connector?.pusher) {
        console.log('[Chat] Attempting reconnect...');
        setConnectionStatus(CONNECTION_STATUS.CONNECTING);
        echoRef.current.connector.pusher.connect();
      }
    }, delay);
  }, []);

  // Initialize Laravel Echo
  useEffect(() => {
    if (!config || !isAuthenticated || !hasChatPermission || !config.pusher?.key) return;

    // Create Echo instance if not exists
    if (!echoRef.current) {
      console.log('[Chat] Initializing Echo with config:', config.pusher);
      setConnectionStatus(CONNECTION_STATUS.CONNECTING);

      echoRef.current = new Echo({
        broadcaster: 'pusher',
        key: config.pusher.key,
        cluster: config.pusher.cluster || 'mt1',
        wsHost: config.pusher.wsHost,
        wsPort: config.pusher.wsPort,
        wssPort: config.pusher.wsPort,
        forceTLS: config.pusher.forceTLS === true,
        encrypted: config.pusher.forceTLS === true,
        disableStats: true,
        enabledTransports: ['ws', 'wss'],
        authEndpoint: `${config.apiUrl}/broadcasting/auth`,
        auth: {
          // Dynamic token getter - always reads fresh token
          headers: {
            get Authorization() {
              return `Bearer ${cookies.get('accessToken')}`;
            },
            Accept: 'application/json',
          },
        },
      });

      const connection = echoRef.current.connector.pusher.connection;

      // Handle successful connection
      connection.bind('connected', () => {
        console.log('[Chat] WebSocket connected!');
        setConnectionStatus(prev => {
          // If we were disconnected, refresh data to fill gaps
          if (prev === CONNECTION_STATUS.DISCONNECTED || prev === CONNECTION_STATUS.CONNECTING) {
            refreshCurrentMessages();
            fetchConversations();
          }
          return CONNECTION_STATUS.CONNECTED;
        });
        setReconnectAttempts(0);
        // Clear any pending reconnect timeout
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
      });

      // Handle disconnection - schedule reconnect
      connection.bind('disconnected', () => {
        console.log('[Chat] WebSocket disconnected');
        setConnectionStatus(CONNECTION_STATUS.DISCONNECTED);
        setReconnectAttempts(prev => {
          scheduleReconnect(prev);
          return prev + 1;
        });
      });

      // Handle unavailable state (couldn't connect at all)
      connection.bind('unavailable', () => {
        console.log('[Chat] WebSocket unavailable');
        setConnectionStatus(CONNECTION_STATUS.DISCONNECTED);
        setReconnectAttempts(prev => {
          scheduleReconnect(prev);
          return prev + 1;
        });
      });

      // Handle errors
      connection.bind('error', (err) => {
        console.error('[Chat] WebSocket error:', err);
        // Auth errors should trigger immediate reconnect attempt with fresh token
        if (err?.error?.data?.code === 4001 || err?.type === 'AuthError') {
          console.log('[Chat] Auth error, reconnecting with fresh token...');
          setReconnectAttempts(0);
          scheduleReconnect(0);
        }
      });

      // Debug state changes
      connection.bind('state_change', (states) => {
        console.log('[Chat] WebSocket state:', states.previous, '->', states.current);
      });
    }

    return () => {
      // Don't disconnect on re-render, only on unmount
    };
  }, [config, isAuthenticated, hasChatPermission, scheduleReconnect]);

  // Subscribe to all conversation channels for real-time updates
  useEffect(() => {
    if (!echoRef.current) return;

    // Get current conversation IDs as a Set for efficient lookup
    const currentConversationIds = new Set(conversations.map(c => c.id));

    // Unsubscribe from conversations that are no longer in the list
    subscribedConversationsRef.current.forEach(id => {
      if (!currentConversationIds.has(id)) {
        console.log('[Chat] Unsubscribing from removed conversation:', id);
        echoRef.current.leave(`conversation.${id}`);
        subscribedConversationsRef.current.delete(id);
      }
    });

    if (conversations.length === 0) return;

    console.log('[Chat] Subscribing to', conversations.length, 'conversation channels');

    conversations.forEach(conv => {
      if (subscribedConversationsRef.current.has(conv.id)) return;

      console.log('[Chat] Subscribing to conversation:', conv.id);
      const channel = echoRef.current.private(`conversation.${conv.id}`);

      // Handle subscription errors
      channel.error((error) => {
        console.error('[Chat] Channel subscription error:', error);
      });

      channel
        .listen('.message.new', (data) => {
          console.log('[Chat] Received message.new event:', data);
          if (data.message) {
            // Skip if this is our own message (already added via API response)
            const senderId = String(data.message.sender?.id || data.message.userId);
            if (senderId === String(currentUserId)) {
              console.log('[Chat] Skipping own message from WebSocket');
              return;
            }

            // Check if this message is for the currently selected conversation (use string comparison)
            const messageConvId = String(data.message.conversationId);
            const selectedConvId = String(selectedConversationRef.current?.id || '');
            const isSelectedConversation = selectedConvId && selectedConvId === messageConvId;
            const isUrgent = data.message.isUrgent || data.message.is_urgent;

            // Check if user is actually on the chat page
            const isOnChatPage = window.location.pathname.includes('/chat');
            const isActivelyViewingConversation = isSelectedConversation && isOnChatPage;

            // Check if this conversation is muted
            const messageConversation = conversationsRef.current?.find(c => String(c.id) === messageConvId);
            const mutedUntil = messageConversation?.currentUserParticipant?.mutedUntil;
            const isMuted = mutedUntil && new Date(mutedUntil) > new Date();

            // Play appropriate notification sound (unless muted)
            if (!isMuted) {
              if (isUrgent) {
                playUrgentSound();
                // Show fullscreen urgent message
                setUrgentMessage(data.message);
              } else if (isActivelyViewingConversation) {
                playInChatSound();
              } else {
                playNotificationSound();
              }
            }

            // Add message to list if it's for the selected conversation (using Set for O(1) duplicate check)
            if (isSelectedConversation) {
              setMessages(prev => {
                if (messageIdsRef.current.has(data.message.id)) return prev;
                messageIdsRef.current.add(data.message.id);
                return [data.message, ...prev];
              });
            }

            // Update conversation list (use string comparison for IDs)
            setConversations(prev => prev.map(c => {
              if (String(c.id) === messageConvId) {
                return {
                  ...c,
                  latestMessage: data.message,
                  lastMessageAt: data.message.createdAt,
                  // Only keep unread at 0 if actively viewing this conversation on chat page
                  unreadCount: isActivelyViewingConversation ? c.unreadCount : (c.unreadCount || 0) + 1,
                };
              }
              return c;
            }));

            // Show notification popup if NOT actively viewing the conversation
            // unreadTotal is computed automatically via useMemo from conversations
            if (!isActivelyViewingConversation) {
              const conversation = conversationsRef.current.find(c => c.id === data.message.conversationId);
              showNotification(data.message, conversation);
            }
          }
        })
        .listen('.message.updated', (data) => {
          console.log('[Chat] Received message.updated event:', data);
          if (data.message) {
            setMessages(prev => prev.map(m => m.id === data.message.id ? data.message : m));
          }
        })
        .listen('.message.deleted', (data) => {
          console.log('[Chat] Received message.deleted event:', data);
          if (data.messageId) {
            messageIdsRef.current.delete(data.messageId);
            setMessages(prev => prev.filter(m => m.id !== data.messageId));
          }
        })
        .listen('.user.typing', (data) => {
          if (data.userId !== currentUserId) {
            handleTypingIndicator(data.userId, data.userName);
          }
        });

      subscribedConversationsRef.current.add(conv.id);
    });
    // Only re-run when conversation IDs change, not on every update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationIdsKey, currentUserId]);

  // Cleanup Echo and timeouts on unmount
  useEffect(() => {
    return () => {
      // Clear reconnect timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      // Clear typing timeouts
      Object.values(typingTimeoutRef.current).forEach(clearTimeout);
      typingTimeoutRef.current = {};

      // Clear notification timeouts
      notificationTimeoutsRef.current.forEach(clearTimeout);
      notificationTimeoutsRef.current.clear();

      // Disconnect Echo
      if (echoRef.current) {
        echoRef.current.disconnect();
        echoRef.current = null;
        subscribedConversationsRef.current.clear();
      }

      // Clear message IDs set
      messageIdsRef.current.clear();
    };
  }, []);

  return (
    <ChatClientContext.Provider
      value={{
        conversations,
        selectedConversation,
        messages,
        unreadTotal,
        isLoading,
        isLoadingConversations,
        hasMoreMessages,
        typingUsers,
        replyToMessage,
        setSelectedConversation,
        sendMessage,
        loadMoreMessages,
        createConversation,
        findOrCreateDirect,
        markAsRead,
        markAllAsRead,
        sendTypingIndicator,
        handleTypingIndicator,
        refreshConversations: fetchConversations,
        addMessage,
        updateMessage,
        deleteMessage,
        notifications,
        dismissNotification,
        setReplyToMessage,
        addReaction,
        removeReaction,
        updateConversation,
        pendingFiles,
        setPendingFiles,
        urgentMessage,
        dismissUrgentMessage,
        connectionStatus,
      }}
    >
      {children}
    </ChatClientContext.Provider>
  );
};

export const useChatClient = () => useContext(ChatClientContext);
